import React, { memo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
} from '@mui/material';
import { Close as CloseIcon, FilterAlt as FilterIcon } from '@mui/icons-material';
import type { ShiftFilterCondition } from '../../hooks/useShiftFilter';

/**
 * FilterDrawer Props
 */
interface FilterDrawerProps {
  /** ドロワー表示状態 */
  open: boolean;
  /** 閉じるハンドラー */
  onClose: () => void;
  /** 現在のフィルター状態 */
  filter: ShiftFilterCondition;
  /** フィルターが適用されているか */
  isFiltering: boolean;
  /** 医師キーワード変更ハンドラー */
  onDoctorKeywordChange: (keyword: string) => void;
  /** 時間帯開始変更ハンドラー */
  onTimeFromChange: (time: string) => void;
  /** 時間帯終了変更ハンドラー */
  onTimeToChange: (time: string) => void;
  /** リセットハンドラー */
  onReset: () => void;
}

/**
 * フィルタードロワーコンポーネント
 *
 * @description
 * REQ-005に基づく絞り込み機能のUIを提供する。
 * 医師名（部分一致）、時間帯での絞り込みをサポート。
 *
 * @param {FilterDrawerProps} props - コンポーネントProps
 * @returns {React.ReactElement} フィルタードロワー
 *
 * @example
 * <FilterDrawer
 *   open={isFilterOpen}
 *   onClose={closeFilter}
 *   filter={filter}
 *   isFiltering={isFiltering}
 *   onDoctorKeywordChange={setDoctorKeyword}
 *   onTimeFromChange={setTimeFrom}
 *   onTimeToChange={setTimeTo}
 *   onReset={resetFilter}
 * />
 */
const FilterDrawer: React.FC<FilterDrawerProps> = memo(
  ({
    open,
    onClose,
    filter,
    isFiltering,
    onDoctorKeywordChange,
    onTimeFromChange,
    onTimeToChange,
    onReset,
  }) => {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: 320 } }}
      >
        <Box sx={{ p: 2 }}>
          {/* ヘッダー */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterIcon color="primary" />
              <Typography variant="h6">絞り込み</Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* フィルター入力フィールド */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* 医師名/ID検索 */}
            <TextField
              label="医師名 / 医師ID"
              placeholder="例: 山田 または D001"
              value={filter.doctorKeyword}
              onChange={(e) => onDoctorKeywordChange(e.target.value)}
              size="small"
              fullWidth
              helperText="部分一致で検索"
            />

            {/* 時間帯（From） */}
            <TextField
              label="時間帯（開始）"
              type="time"
              value={filter.timeFrom}
              onChange={(e) => onTimeFromChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 900 }}
              size="small"
              fullWidth
              helperText="この時間以降のシフトを表示"
            />

            {/* 時間帯（To） */}
            <TextField
              label="時間帯（終了）"
              type="time"
              value={filter.timeTo}
              onChange={(e) => onTimeToChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 900 }}
              size="small"
              fullWidth
              helperText="この時間以前のシフトを表示"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* アクションボタン */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={onReset}
              disabled={!isFiltering}
              fullWidth
            >
              リセット
            </Button>
            <Button variant="contained" onClick={onClose} fullWidth>
              閉じる
            </Button>
          </Box>
        </Box>
      </Drawer>
    );
  }
);

FilterDrawer.displayName = 'FilterDrawer';

export { FilterDrawer };
